import { useEffect, useRef, useState } from "react";
import { stats } from "@/mocks/homeData";
import { useScrollReveal } from "@/hooks/useScrollReveal";

function CountUp({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 1200;
          const step = Math.ceil(target / (duration / 16));
          const timer = setInterval(() => {
            start += step;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(start);
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

export default function StatsSection() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollReveal();
  const { ref: rightRef, isVisible: rightVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="py-20 lg:py-24 bg-[#f9f7f4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <div
            ref={leftRef}
            className={`transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
              Trusted by Enterprises
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-6">
              A Decade of Delivering <br />
              <strong>Solutions That Work</strong>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mb-8">
              For over 12 years, we've been the trusted partner for enterprises across security, construction, healthcare, and government sectors — delivering end-to-end solutions from the world's most respected hardware brands.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/about"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1a1a1a] border border-[#1a1a1a] px-5 py-2.5 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                About Our Company
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer px-5 py-2.5"
              >
                View Case Studies
              </a>
            </div>
          </div>

          {/* Right: Stats Grid */}
          <div ref={rightRef} className="grid grid-cols-2 gap-8">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`bg-white rounded-lg p-6 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: rightVisible ? `${i * 100}ms` : "0ms" }}
              >
                <p className="text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-2">
                  <CountUp target={parseInt(s.value)} suffix={s.suffix} />
                </p>
                <p className="text-sm text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
